"use client";

import { useMediaQuery } from "@mantine/hooks";
import { MainButton, useAppContext } from "@repo/dashboard-core";
import Image from "next/image";
import Link from "next/link";
import { useLocale, useTranslations } from "next-intl";

import { openSubscriptionModal } from "@/components/subscription/SubscriptionModal";

/**
 * `preview` names the screenshot behind the paywall — Send SMS uses the unnamed
 * one. The teasers are rasters, so the text in them is frozen at the locale they
 * were shot in, and each surface carries one file per locale (GD-86). All of
 * them come out of `pnpm shoot:paywall`, which shoots the real pages signed in
 * as a real member; nothing here is hand-cropped.
 */
export function ServiceDummy({ preview }: { preview?: string }) {
  const t = useTranslations();
  const locale = useLocale();
  const { subscriptionsInfo, userInfo } = useAppContext();
  const isMobile = useMediaQuery("(max-width: 767px)");
  const variant = isMobile ? "mobile" : "desktop";
  const fileName = `${preview ? `${preview}-` : ""}${locale}-${variant}.jpg`;

  const subscriptions = subscriptionsInfo?.runningSubscriptions ?? [];

  return (
    <div className="flex w-full items-center justify-center">
      <div className="relative w-full max-w-6xl">
        <Image
          src={`/img/tools/${fileName}`}
          alt={t("tools.preview-alt")}
          width={1200}
          height={800}
          className="h-auto w-full rounded-xl object-contain grayscale"
          priority
        />

        {/* Overlay */}
        <div className="absolute inset-0 flex flex-col items-center justify-center rounded-xl bg-black/40 p-4">
          <div className="w-full max-w-md rounded-xl bg-pureWhite p-6 text-center shadow-shell-lg">
            <p className="mb-4 text-text-primary">
              {t("tools.subscribe-info")}
            </p>
            {subscriptions.length > 0 ? (
              <MainButton
                onClick={() =>
                  openSubscriptionModal({
                    subscriptions,
                    customerEmail: userInfo.email,
                  })
                }
              >
                {t("tools.subscribe-cta")}
              </MainButton>
            ) : (
              // No subscription to resume — send the user to the dashboard
              <Link href={`/${locale}`}>
                <MainButton>{t("tools.subscribe-cta")}</MainButton>
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
